import type { StorefrontStory, StorefrontStoryMedia } from '@erp/shared';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ImageOff, ShoppingBag, Volume2, VolumeX, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useDirection } from '../../hooks/useDirection.js';
import { formatMoney } from '../../lib/money.js';
import { assetUrl, publicApi } from '../../lib/publicApi.js';
import { useStoriesSeen } from '../../lib/storiesSeen.js';

// Fullscreen Instagram-style story player for the web storefront.
//
// One rAF clock drives BOTH the progress bar and the auto-advance (no separate
// CSS transition to drift out of sync). Video progress reads the element's own
// currentTime, so buffering stalls the bar instead of cutting the clip short;
// image timing starts only once the image has LOADED (spinner until then).
// Gestures (hand-rolled pointer tracking, no library): tap zones, hold-to-pause,
// swipe down to dismiss, horizontal swipe between stories, swipe up for tagged
// products. Keyboard: arrows (RTL-flipped), Space pauses, Esc closes. The next
// media is preloaded so advancing never flashes black.

const IMAGE_MS = 5000;
const VIDEO_FALLBACK_MS = 15000;
/** Movement below this is a tap; beyond it the pointer becomes a drag. */
const DRAG_SLOP_PX = 12;

function effectiveMs(m: StorefrontStoryMedia): number {
  if (m.durationMs > 0) return m.durationMs;
  return m.type === 'video' ? VIDEO_FALLBACK_MS : IMAGE_MS;
}

interface Props {
  slug: string;
  /** Ordered playlist of stories to play through. */
  stories: StorefrontStory[];
  startId: string;
  onClose: () => void;
}

interface Gesture {
  pointerId: number;
  x0: number;
  y0: number;
  t0: number;
  /** Locked once movement exceeds the slop — the dominant direction wins. */
  axis: 'x' | 'y' | null;
  dx: number;
  dy: number;
  held: boolean;
  holdTimer: number | null;
}

export function StoryViewer({ slug, stories, startId, onClose }: Props): JSX.Element | null {
  const { t, i18n } = useTranslation('cms');
  const dir = useDirection();
  const markSeen = useStoriesSeen((s) => s.markSeen);

  const startIdx = Math.max(0, stories.findIndex((s) => s.id === startId));
  const [storyIdx, setStoryIdx] = useState(startIdx);
  const [mediaIdx, setMediaIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [muted, setMuted] = useState(true);
  const [ready, setReady] = useState(false);
  const [showProducts, setShowProducts] = useState(false);

  const story = stories[storyIdx];
  const media = story?.media[mediaIdx];
  const mediaKey = `${storyIdx}:${mediaIdx}`;
  const duration = media ? effectiveMs(media) : IMAGE_MS;

  // ── clock: elapsed ms within the current IMAGE (videos track their own time).
  // startedAtRef > 0 ⇔ the clock is running; elapsedRef accumulates across pauses.
  const elapsedRef = useRef(0);
  const startedAtRef = useRef(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  const closingRef = useRef(false);

  const running = ready && !paused && !dragging;
  // The rAF loop reads this to LAZILY start the clock (see below) — starting on
  // a `running` transition alone misses cached/preloaded media, whose ready
  // flag flips false→true inside one render batch and never transitions.
  const runningRef = useRef(false);
  runningRef.current = running;

  // Stop the image clock when interaction pauses it (accumulate the elapsed part).
  useEffect(() => {
    if (!running && startedAtRef.current > 0) {
      elapsedRef.current += performance.now() - startedAtRef.current;
      startedAtRef.current = 0;
    }
  }, [running]);

  const playedMs = (): number =>
    elapsedRef.current + (startedAtRef.current > 0 ? performance.now() - startedAtRef.current : 0);

  /** Advance one media step (tap zones, keyboard, auto-advance). */
  const go = useCallback((step: 1 | -1): void => {
    setShowProducts(false);
    const st = stories[storyIdx];
    if (!st) { onClose(); return; }
    if (step === 1) {
      if (mediaIdx + 1 < st.media.length) setMediaIdx(mediaIdx + 1);
      else if (storyIdx + 1 < stories.length) { setStoryIdx(storyIdx + 1); setMediaIdx(0); }
      else onClose();
      return;
    }
    // Back after >1.5s of playback restarts the current clip (Instagram-style);
    // the elapsed time is read BEFORE any reset so the branch can actually fire.
    if (playedMs() > 1500 || (mediaIdx === 0 && storyIdx === 0)) {
      elapsedRef.current = 0;
      if (startedAtRef.current > 0) startedAtRef.current = performance.now();
      if (videoRef.current) videoRef.current.currentTime = 0;
      if (barRef.current) barRef.current.style.width = '0%';
      return;
    }
    if (mediaIdx > 0) { setMediaIdx(mediaIdx - 1); return; }
    const prev = stories[storyIdx - 1]!;
    setStoryIdx(storyIdx - 1);
    setMediaIdx(Math.max(0, prev.media.length - 1));
  }, [stories, storyIdx, mediaIdx, onClose]);
  const goRef = useRef(go);
  useEffect(() => { goRef.current = go; });

  /** Jump a whole STORY (horizontal swipe). */
  const goStory = useCallback((step: 1 | -1): void => {
    setShowProducts(false);
    if (step === 1) {
      if (storyIdx + 1 < stories.length) { setStoryIdx(storyIdx + 1); setMediaIdx(0); }
      else onClose();
    } else if (storyIdx > 0) {
      setStoryIdx(storyIdx - 1);
      setMediaIdx(0);
    }
  }, [stories.length, storyIdx, onClose]);

  // Reset per-media state when the clip changes.
  useEffect(() => {
    elapsedRef.current = 0;
    startedAtRef.current = 0;
    setReady(false);
    setShowProducts(false);
    if (barRef.current) barRef.current.style.width = '0%';
    // A cached image may already be complete — onLoad won't re-fire.
    if (imgRef.current?.complete) setReady(true);
  }, [mediaKey]);

  // ── the single clock: one rAF loop paints the bar and advances images ──
  useEffect(() => {
    if (!media) return undefined;
    let raf = 0;
    let advanced = false;
    const type = media.type;
    const tick = (): void => {
      let pct = 0;
      if (type === 'video') {
        const v = videoRef.current;
        const durMs = v && Number.isFinite(v.duration) && v.duration > 0 ? v.duration * 1000 : duration;
        pct = v ? Math.min(100, ((v.currentTime * 1000) / durMs) * 100) : 0;
      } else {
        // Lazy clock start: the first frame where the clip is allowed to run
        // arms it — regardless of HOW it became runnable (fresh load, cache
        // hit, pause release, media change).
        if (runningRef.current && startedAtRef.current === 0) startedAtRef.current = performance.now();
        const elapsed = playedMs();
        pct = Math.min(100, (elapsed / duration) * 100);
        if (elapsed >= duration && !advanced && !closingRef.current) {
          advanced = true;
          goRef.current(1);
        }
      }
      if (barRef.current) barRef.current.style.width = `${pct}%`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaKey, duration]);

  // Preload the upcoming media (next clip, then the next story's opener) so
  // advancing never flashes black. Images + posters only — videos stream.
  useEffect(() => {
    const upcoming = [story?.media[mediaIdx + 1], stories[storyIdx + 1]?.media[0]];
    for (const m of upcoming) {
      if (!m) continue;
      const urls = [m.type === 'image' ? assetUrl(m.url) : null, assetUrl(m.posterUrl)];
      for (const u of urls) {
        if (u) { const im = new Image(); im.src = u; }
      }
    }
  }, [mediaKey, stories, story, storyIdx, mediaIdx]);

  // Mark the current story seen the moment it shows, and count the view.
  //
  // Once per story per open, not once per render: `story` only changes when the
  // player actually moves on, and swiping back to one already counted in this
  // session doesn't count it twice. Fire-and-forget — a shop's view counter must
  // never be able to break the player, and the server ignores a ping for a story
  // that isn't live.
  const countedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!story) return;
    markSeen(slug, story.id);
    if (countedRef.current.has(story.id)) return;
    countedRef.current.add(story.id);
    void publicApi().post(`/${slug}/stories/${story.id}/view`).catch(() => undefined);
  }, [story, slug, markSeen]);

  // Body scroll lock while the viewer is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const setVideoPaused = (p: boolean): void => {
    if (!videoRef.current) return;
    if (p) videoRef.current.pause();
    else void videoRef.current.play().catch(() => undefined);
  };
  const togglePause = useCallback((): void => {
    setPaused((p) => { setVideoPaused(!p); return !p; });
  }, []);

  // Keyboard: Esc closes; arrows follow reading direction; Space pauses.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); togglePause(); return; }
      const fwd = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
      const back = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
      if (e.key === fwd) go(1);
      else if (e.key === back) go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose, dir, togglePause]);

  // ── pointer gestures: tap / hold / swipe (window-level listeners per gesture,
  // so releases outside the stage still land; no pointer capture, so the
  // chrome's real buttons keep working untouched) ──
  const gestureRef = useRef<Gesture | null>(null);
  // Teardown for the active gesture's window listeners (run on unmount too).
  const detachRef = useRef<(() => void) | null>(null);

  const snapBack = useCallback((): void => {
    const el = stageRef.current;
    if (!el) return;
    el.style.transition = 'transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1)';
    el.style.transform = '';
    if (backdropRef.current) { backdropRef.current.style.transition = 'opacity 200ms'; backdropRef.current.style.opacity = '1'; }
    window.setTimeout(() => { if (el) el.style.transition = ''; }, 220);
  }, []);

  const dismissWithDrag = useCallback((): void => {
    if (closingRef.current) return;
    closingRef.current = true;
    const el = stageRef.current;
    if (el) {
      el.style.transition = 'transform 200ms ease-in, opacity 200ms ease-in';
      el.style.transform = 'translateY(90vh) scale(0.9)';
      el.style.opacity = '0';
    }
    if (backdropRef.current) { backdropRef.current.style.transition = 'opacity 200ms'; backdropRef.current.style.opacity = '0'; }
    window.setTimeout(onClose, 190);
  }, [onClose]);

  const endGesture = useCallback((e: PointerEvent): void => {
    const g = gestureRef.current;
    if (!g || e.pointerId !== g.pointerId) return;
    gestureRef.current = null;
    if (g.holdTimer != null) window.clearTimeout(g.holdTimer);
    setDragging(false);

    if (g.axis === 'y') {
      const dt = Math.max(1, performance.now() - g.t0);
      if (g.dy > 120 || g.dy / dt > 0.55) { dismissWithDrag(); return; }
      if (g.dy < -60) setShowProducts((story?.products.length ?? 0) > 0);
      snapBack();
      return;
    }
    if (g.axis === 'x') {
      const dt = Math.max(1, performance.now() - g.t0);
      const fast = Math.abs(g.dx) / dt > 0.55;
      if (Math.abs(g.dx) > 64 || fast) {
        // Swiping the card toward the reading start reveals the NEXT story.
        const forward = dir === 'rtl' ? g.dx > 0 : g.dx < 0;
        if (stageRef.current) stageRef.current.style.transform = '';
        goStory(forward ? 1 : -1);
        if (backdropRef.current) backdropRef.current.style.opacity = '1';
        return;
      }
      snapBack();
      return;
    }
    // No axis locked: a hold released resumes; a plain tap navigates by side.
    if (g.held) { setPaused(false); setVideoPaused(false); return; }
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    const rel = (g.x0 - rect.left) / rect.width;
    const backZone = dir === 'rtl' ? rel > 0.68 : rel < 0.32;
    go(backZone ? -1 : 1);
  }, [dir, go, goStory, story, dismissWithDrag, snapBack]);
  // The per-gesture window listeners call through this ref so a media change
  // mid-gesture (auto-advance) can't strand them on a stale closure.
  const endGestureRef = useRef(endGesture);
  useEffect(() => { endGestureRef.current = endGesture; });

  const moveGesture = useCallback((e: PointerEvent): void => {
    const g = gestureRef.current;
    if (!g || e.pointerId !== g.pointerId) return;
    g.dx = e.clientX - g.x0;
    g.dy = e.clientY - g.y0;
    if (g.axis === null) {
      if (Math.hypot(g.dx, g.dy) < DRAG_SLOP_PX) return;
      g.axis = Math.abs(g.dx) > Math.abs(g.dy) ? 'x' : 'y';
      if (g.holdTimer != null) { window.clearTimeout(g.holdTimer); g.holdTimer = null; }
      if (g.held) { g.held = false; setPaused(false); setVideoPaused(false); }
      setDragging(true);
    }
    const el = stageRef.current;
    if (!el) return;
    if (g.axis === 'y') {
      const dy = Math.max(0, g.dy); // only downward pulls move the card
      el.style.transform = dy > 0 ? `translateY(${dy}px) scale(${Math.max(0.85, 1 - dy / 1200)})` : '';
      if (backdropRef.current) {
        backdropRef.current.style.transition = ''; // live drags dim instantly (snapBack leaves a transition behind)
        backdropRef.current.style.opacity = String(Math.max(0.4, 1 - dy / 600));
      }
    } else {
      el.style.transform = `translateX(${g.dx}px) rotate(${g.dx / 60}deg)`;
    }
  }, []);

  // Unmount safety: drop the active gesture's window listeners.
  useEffect(() => () => { detachRef.current?.(); }, []);

  const onStagePointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (e.button > 0 || gestureRef.current || closingRef.current) return;
    // Real controls (close, mute, product links, the scrollable sheet) win.
    if ((e.target as HTMLElement).closest('button, a, [data-scroll]')) return;
    const g: Gesture = { pointerId: e.pointerId, x0: e.clientX, y0: e.clientY, t0: performance.now(), axis: null, dx: 0, dy: 0, held: false, holdTimer: null };
    g.holdTimer = window.setTimeout(() => {
      g.holdTimer = null;
      g.held = true;
      setPaused(true);
      setVideoPaused(true);
    }, 200);
    gestureRef.current = g;
    const up = (ev: PointerEvent): void => {
      detach();
      endGestureRef.current(ev);
    };
    const detach = (): void => {
      window.removeEventListener('pointermove', moveGesture);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (g.holdTimer != null) window.clearTimeout(g.holdTimer);
      detachRef.current = null;
    };
    detachRef.current = detach;
    window.addEventListener('pointermove', moveGesture);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const products = story?.products ?? [];
  const chipProducts = products.slice(0, 2);
  const extraCount = products.length - chipProducts.length;
  const money = (p: { price: string; currencyCode: string }): string =>
    formatMoney(p.price, { locale: i18n.language, currency: p.currencyCode });

  // Story→entity CTA: navigate to the linked product/category/brand page.
  const link = story?.link ?? null;
  const linkTarget = link
    ? link.type === 'brand'
      ? `/s/${slug}/brand/${link.id}`
      : `/s/${slug}/${link.type}/${link.slug ?? link.id}`
    : null;
  const linkLabel = link
    ? link.type === 'product'
      ? t('store.stories.viewProduct', { defaultValue: 'View product' })
      : link.type === 'category'
        ? t('store.stories.viewCategory', { defaultValue: 'View category' })
        : t('store.stories.viewBrand', { defaultValue: 'View brand' })
    : '';

  if (!story || !media) {
    return createPortal(
      <div className="fixed inset-0 z-[80] grid place-items-center bg-black/90" onClick={onClose}>
        <div className="flex flex-col items-center gap-3 text-white/80">
          <ImageOff className="h-8 w-8" aria-hidden />
          <p className="text-sm">{t('store.stories.unavailable', { defaultValue: 'This story is no longer available.' })}</p>
        </div>
      </div>,
      document.body,
    );
  }

  const src = assetUrl(media.url) ?? '';

  return createPortal(
    <div className="fixed inset-0 z-[80]" dir={dir} role="dialog" aria-modal="true" aria-label={story.title ?? t('store.stories.title', { defaultValue: 'Stories' })}>
      <div ref={backdropRef} className="absolute inset-0 bg-black/95" aria-hidden />
      {/* backdrop click closes (outside the stage) */}
      <button type="button" className="absolute inset-0 cursor-default" onClick={onClose} aria-label={t('store.stories.close', { defaultValue: 'Close' })} tabIndex={-1} />

      {/* desktop prev/next (mobile navigates by tap zones + swipes + arrow keys) */}
      <button
        type="button"
        onClick={() => go(-1)}
        className="absolute start-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/25 sm:grid"
        aria-label={t('store.stories.previous', { defaultValue: 'Previous' })}
      >
        <ChevronLeft className="h-6 w-6 rtl:-scale-x-100" aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        className="absolute end-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/25 sm:grid"
        aria-label={t('store.stories.next', { defaultValue: 'Next' })}
      >
        <ChevronRight className="h-6 w-6 rtl:-scale-x-100" aria-hidden />
      </button>

      <div className="absolute inset-0 mx-auto flex h-full w-full max-w-[min(100vw,56vh)] flex-col justify-center">
        <div
          ref={stageRef}
          className="relative aspect-[9/16] max-h-full w-full touch-none select-none overflow-hidden bg-black will-change-transform sm:rounded-2xl"
          onPointerDown={onStagePointerDown}
        >
          {media.type === 'video' ? (
            <video
              key={mediaKey}
              ref={videoRef}
              src={src}
              poster={assetUrl(media.posterUrl) ?? undefined}
              autoPlay
              playsInline
              muted={muted}
              onPlaying={() => setReady(true)}
              onCanPlay={() => setReady(true)}
              onEnded={() => { if (!closingRef.current) go(1); }}
              onError={() => { if (!closingRef.current) go(1); }}
              className="h-full w-full object-contain duration-300 animate-in fade-in-0 motion-reduce:animate-none"
              draggable={false}
            />
          ) : (
            <img
              key={mediaKey}
              ref={imgRef}
              src={src}
              alt={story.title ?? ''}
              onLoad={() => setReady(true)}
              onError={() => { if (!closingRef.current) go(1); }}
              className="h-full w-full object-contain duration-300 animate-in fade-in-0 motion-reduce:animate-none"
              draggable={false}
            />
          )}

          {/* buffering / loading spinner (clock only runs once ready) */}
          {!ready && (
            <div className="absolute inset-0 grid place-items-center" aria-hidden>
              <span className="h-9 w-9 animate-spin rounded-full border-2 border-white/25 border-t-white motion-reduce:animate-none" />
            </div>
          )}

          {/* top chrome */}
          <div className={`pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-black/70 to-transparent p-3 pb-8 transition-opacity ${paused && !dragging ? 'opacity-0' : 'opacity-100'}`}>
            <div className="flex gap-1">
              {story.media.map((_, i) => (
                <div key={i} className="h-1 flex-1 overflow-hidden rounded-full bg-white/30">
                  <div
                    ref={i === mediaIdx ? barRef : undefined}
                    className="h-full rounded-full bg-white"
                    style={{ width: i < mediaIdx ? '100%' : '0%' }}
                  />
                </div>
              ))}
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <div className="min-w-0">
                {story.title && <div className="truncate text-sm font-semibold text-white drop-shadow">{story.title}</div>}
              </div>
              <div className="pointer-events-auto flex items-center gap-1">
                {media.type === 'video' && (
                  <button type="button" onClick={() => setMuted((m) => !m)} className="grid h-9 w-9 place-items-center rounded-full bg-black/40 text-white transition hover:bg-black/60" aria-label={t('store.stories.mute', { defaultValue: 'Mute' })}>
                    {muted ? <VolumeX className="h-4 w-4" aria-hidden /> : <Volume2 className="h-4 w-4" aria-hidden />}
                  </button>
                )}
                <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full bg-black/40 text-white transition hover:bg-black/60" aria-label={t('store.stories.close', { defaultValue: 'Close' })}>
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </div>
          </div>

          {/* bottom chrome: caption + tagged products + entity CTA */}
          {(story.caption || products.length > 0 || (link && linkTarget)) && (
            <div className={`pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-3 pt-10 transition-opacity ${paused && !dragging ? 'opacity-0' : 'opacity-100'}`}>
              {story.caption && <p className="mb-2 line-clamp-2 text-sm text-white/90 drop-shadow">{story.caption}</p>}
              {link && linkTarget && (
                <Link
                  to={linkTarget}
                  onClick={onClose}
                  className="pointer-events-auto mb-2 flex items-center gap-2 rounded-xl bg-white/95 p-1.5 pe-3 text-neutral-900 shadow transition hover:bg-white"
                >
                  {link.image ? (
                    <img src={assetUrl(link.image) ?? undefined} alt="" className="h-10 w-10 flex-none rounded-lg object-cover" />
                  ) : (
                    <span className="grid h-10 w-10 flex-none place-items-center rounded-lg bg-neutral-200 text-neutral-500">
                      <ShoppingBag className="h-5 w-5" aria-hidden />
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{link.label}</span>
                    <span className="block text-[11px] font-medium text-neutral-500">{linkLabel}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 flex-none rtl:-scale-x-100" aria-hidden />
                </Link>
              )}
              {products.length > 0 && (
                <div className="pointer-events-auto flex flex-wrap items-center gap-2">
                  {chipProducts.map((p) => (
                    <Link
                      key={p.id}
                      to={`/s/${slug}/product/${p.slug ?? p.id}`}
                      onClick={onClose}
                      className="inline-flex max-w-[70%] items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-neutral-900 shadow transition hover:bg-white"
                    >
                      <span className="truncate">{p.name}</span>
                      <span className="shrink-0 font-mono tabular-nums text-neutral-500">{money(p)}</span>
                    </Link>
                  ))}
                  {extraCount > 0 && (
                    <button type="button" onClick={() => setShowProducts((v) => !v)} className="inline-flex items-center gap-1 rounded-full bg-white/25 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition hover:bg-white/35">
                      +{extraCount}
                      {showProducts ? <ChevronDown className="h-3.5 w-3.5" aria-hidden /> : <ChevronUp className="h-3.5 w-3.5" aria-hidden />}
                    </button>
                  )}
                </div>
              )}
              {showProducts && (
                <div data-scroll className="pointer-events-auto mt-2 flex max-h-48 touch-pan-y flex-col gap-1 overflow-y-auto rounded-xl bg-black/70 p-2 backdrop-blur duration-200 animate-in fade-in-0 slide-in-from-bottom-2 motion-reduce:animate-none">
                  {products.map((p) => (
                    <Link key={p.id} to={`/s/${slug}/product/${p.slug ?? p.id}`} onClick={onClose} className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-xs text-white transition hover:bg-white/10">
                      <span className="truncate">{p.name}</span>
                      <span className="shrink-0 font-mono tabular-nums text-white/70">{money(p)}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
